import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieBanner } from "./components/CookieConsent/CookieBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ScrollToTop } from "./components/ScrollToTop";
import Index from "./pages/Index";
import Ads from "./pages/Ads";
import Locations from "./Locations/views/Locations";
import Recipes from "./Recipes/views/Recipes";
import Coupons from "./Coupons/views/Coupons";
import Events from "./pages/Events";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Notifications from "./pages/Notifications";
import Spotlights from "./pages/Spotlights";
import ForgotPassword from "./pages/ForgotPassword";
import AppRedirect from "./pages/AppRedirect";
import NotFound from "./pages/NotFound";
import { useSiteTheme } from "./SiteInfo/hooks/use-site-theme";
import { TimezoneProvider } from "./SiteInfo/context/TimezoneContext";
import { queryClient } from "./lib/query-client";

const AppShell = () => {
  useSiteTheme();

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="flex-1">
            <ErrorBoundary context="the page">
              <Routes>
                <Route path="/" element={<ErrorBoundary context="Home"><Index /></ErrorBoundary>} />
                <Route path="/terms" element={<ErrorBoundary context="Home"><Index /></ErrorBoundary>} />
                <Route path="/privacy" element={<ErrorBoundary context="Home"><Index /></ErrorBoundary>} />
                <Route path="/ads" element={<ErrorBoundary context="Ads"><Ads /></ErrorBoundary>} />
                <Route path="/locations" element={<ErrorBoundary context="Locations"><Locations /></ErrorBoundary>} />
                <Route path="/recipes" element={<ErrorBoundary context="Recipes"><Recipes /></ErrorBoundary>} />
                <Route path="/coupons" element={<ErrorBoundary context="Coupons"><Coupons /></ErrorBoundary>} />
                <Route path="/events" element={<ErrorBoundary context="Events"><Events /></ErrorBoundary>} />
                <Route path="/about" element={<ErrorBoundary context="About"><About /></ErrorBoundary>} />
                <Route path="/careers" element={<Navigate to="/" replace />} />
                <Route path="/contact" element={<ErrorBoundary context="Contact"><Contact /></ErrorBoundary>} />
                <Route path="/notifications" element={<ErrorBoundary context="Notifications"><Notifications /></ErrorBoundary>} />
                <Route path="/spotlights" element={<ErrorBoundary context="Spotlights"><Spotlights /></ErrorBoundary>} />
                <Route path="/forgot-password" element={<ErrorBoundary context="Forgot Password"><ForgotPassword /></ErrorBoundary>} />
                <Route path="/app" element={<AppRedirect />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
          <CookieBanner />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TimezoneProvider>
        <AppShell />
      </TimezoneProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
